import {
  type WebPagePsdExportRunQuery,
  useExportWebPageToPsdMutation,
  useWebPagePsdExportRunQuery,
} from "src/graphql";
import {
  WEB_PAGE_DESKTOP_WIDTH,
  WEB_PAGE_MOBILE_WIDTH,
} from "src/pages/ad-studio/components/web-page-preview";
import type { LayeredFormat } from "src/pages/ad-studio/psd-export/build-email-layered";
import {
  PSD_EXPORT_TIMEOUT_MS,
  buildLayerFiles,
  pollExportRun,
} from "src/pages/lifecycle-studio/emails/png-export/export-email";

export const WEB_PAGE_DEVICES = [
  { suffix: "desktop", label: "Desktop", width: WEB_PAGE_DESKTOP_WIDTH },
  { suffix: "mobile", label: "Mobile", width: WEB_PAGE_MOBILE_WIDTH },
];

type RunFrame = NonNullable<
  WebPagePsdExportRunQuery["webPagePsdExportRun"]
>["frames"][number];

/** Names each rendered frame after its device, dropping frames with no artifact yet. */
export function webPageLayerFrames(frames: RunFrame[]) {
  return frames.flatMap((frame) => {
    const device = WEB_PAGE_DEVICES.find((d) => d.width === frame.width);
    return frame.url && device
      ? [{ suffix: device.suffix, url: frame.url }]
      : [];
  });
}

/**
 * Renders a web page at every device width and returns a file per width and format,
 * so a bulk download can zip many pages together. Polls in a loop, since a bulk run renders pages one after another.
 */
export async function renderWebPageLayeredFiles({
  fileId,
  stem,
  formats,
  isCancelled,
}: {
  fileId: string;
  stem: string;
  formats: readonly LayeredFormat[];
  isCancelled: () => boolean;
}) {
  const { runId } = (
    await useExportWebPageToPsdMutation.fetcher({
      fileId,
      widths: WEB_PAGE_DEVICES.map((device) => device.width),
    })()
  ).exportWebPageToPsd;
  const run = await pollExportRun({
    fetchRun: async () => {
      const { webPagePsdExportRun } = await useWebPagePsdExportRunQuery.fetcher(
        { id: runId },
      )();
      // A null run means the page was deleted or made private mid-export.
      if (!webPagePsdExportRun)
        throw new Error("Export is no longer available");
      return webPagePsdExportRun;
    },
    timeoutMs: PSD_EXPORT_TIMEOUT_MS,
    isCancelled,
  });
  if (!run) return [];
  const frames = webPageLayerFrames(run.frames);
  if (frames.length === 0) throw new Error("No frames returned");
  return buildLayerFiles(frames, stem, formats);
}
